import { styled } from '@mui/system';
import { Box, Typography } from '@mui/material';

export const StatusContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: '200px',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(1),
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        transform: 'translateX(2px)',
    },
}));

export const StatusIndicator = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.25, 0.5),
    borderRadius: theme.spacing(0.5),
    transition: 'all 0.2s ease-in-out',
}));

export const StatusIcon = styled('span')(({ theme }) => ({
    fontSize: '14px',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    '& svg': {
        width: '16px',
        height: '16px',
    },
}));

// Enhanced icon wrapper with better styling
export const IconWrapper = styled('div')(({ theme }) => ({
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
    transform: 'scale(1.1)',
    transition: 'transform 0.2s ease-in-out',
}));

// Enhanced clock icon wrapper with glow effects
export const ClockIconWrapper = styled('div')(({ color, glowColor }) => ({
    position: 'relative',
    filter: `drop-shadow(0 0 4px ${glowColor || color})`,
    transform: 'scale(1.1)',
    transition: 'all 0.2s ease-in-out',
    '&::after': {
        content: '""',
        position: 'absolute',
        top: '2px',
        left: '2px',
        right: '2px',
        bottom: '2px',
        borderRadius: '50%',
        background: `radial-gradient(circle, ${glowColor || color}20, transparent)`,
        pointerEvents: 'none',
    },
}));

// Face icon wrapper for off duty status
export const FaceIconWrapper = styled('div')(({ theme }) => ({
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
    transform: 'scale(1.1)',
    transition: 'transform 0.2s ease-in-out',
}));

export const StatusText = styled(Typography)(({ theme, status }) => {
    const getTextColor = () => {
        switch (status) {
            case 'clocked-in':
                return theme.colors.status.clockedIn;
            case 'break-completed':
                return theme.colors.status.breakCompleted;
            case 'on-break':
                return theme.colors.status.onBreak;
            case 'break-due':
                return theme.colors.status.breakDue;
            case 'break-overdue':
                return theme.colors.status.breakOverdue;
            case 'late':
                return theme.colors.status.late;
            case 'missed':
                return theme.colors.status.late;
            case 'upcoming':
                return theme.colors.status.upcoming;
            case 'clocked-out':
            default:
                return theme.palette.text.primary;
        }
    };

    const getTextShadow = () => {
        const color = getTextColor();
        // Create a subtle glow effect with text shadow, but not for off duty
        if (status === 'clocked-out') {
            return 'none'; // No glow for off duty
        }
        return `0 0 8px ${color}40, 0 0 4px ${color}20`;
    };

    return {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: getTextColor(),
        whiteSpace: 'nowrap',
        textShadow: getTextShadow(),
        letterSpacing: '0.025em', // Slightly better letter spacing
        textRendering: 'optimizeLegibility', // Better text rendering
        // Add subtle glow effect for better visibility
        filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.1))',
        // Ensure text is always readable
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
    };
});
